import { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, Divider, Grid, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { LoadingButton } from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GetContractsAIModels, GetConversation, GetUserConversations, PostToContractsAI } from "../../../api/contracts";
import { useSnackbar } from "../../../reusable-components/snackbar";
import Markdown from "../../../reusable-components/markdown";
import FileAttachment from "../../../reusable-components/file-attachment/FileAttachment";
import ConversationSidebar from "./ConversationSidebar";

function AskForm({ modelOptions, model, setModel, loading, onSubmit }) {
    const [query, setQuery] = useState('');
    const [files, setFiles] = useState([]);

    const handleSubmit = () => {
        if (!query.trim()) return;
        onSubmit(query, model, files);
    };

    const onDrop = (acceptedFiles) => setFiles(acceptedFiles);

    const { getRootProps, getInputProps, open: openDropzone, isDragActive } =
        useDropzone({
            onDrop, noClick: true, noKeyboard: true,
            accept: {
                'application/pdf': ['.pdf'],
                'image/jpeg': ['.jpeg', '.jpg'],
                'image/png': ['.png'],
                'application/msword': ['.doc'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
            }
        });

    return (
        <>
            <TextField
                label="Question"
                placeholder="e.g. what are typical medical director rates and hours"
                multiline
                minRows={2}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit();
                    }
                }}
            />
            <Box {...getRootProps()} sx={{ cursor: 'pointer' }} onClick={openDropzone}>
                <input {...getInputProps()} />
                <Paper
                    elevation={isDragActive ? 6 : 1}
                    sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        color: 'info.main',
                        borderColor: 'info.main',
                        border: 1,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            bgcolor: 'background.default',
                            boxShadow: 3,
                        },
                    }}
                >
                    <Typography variant="body2" textAlign="center">
                        Click to upload or drag and drop files to include with question
                    </Typography>
                </Paper>
            </Box>
            {files?.length > 0 && <Grid container direction="row" alignItems="flex-start">
                {files.map((file) => (
                    <FileAttachment
                        key={file.name}
                        file={file}
                        fileName={file.name}
                        canDeleteFile
                        handleDeleteFile={() => setFiles(oldFiles => oldFiles.filter(f => f !== file))}
                    />
                ))}
            </Grid>}
            <TextField
                select
                label="Model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                sx={{ maxWidth: 320 }}
                disabled={!modelOptions.length}
            >
                {modelOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>

            <LoadingButton
                variant="contained"
                onClick={handleSubmit}
                loading={loading}
                disabled={!query.trim() || !model}
                sx={{ alignSelf: 'flex-start' }}
            >
                Ask
            </LoadingButton>
        </>
    );
}

AskForm.propTypes = {
    modelOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
    })),
    model: PropTypes.string,
    setModel: PropTypes.func,
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
};

function ResultAccordion({ title, items }) {
    if (!items || (Array.isArray(items) && items.length === 0)) return null;

    return (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {Array.isArray(items)
                    ? items.map((item, index) => (
                        <Markdown key={index} sx={{ mb: index < items.length - 1 ? 1.5 : 0 }}>
                            {typeof item === 'string' ? item : JSON.stringify(item, null, 2)}
                        </Markdown>
                    ))
                    : <Markdown>{typeof items === 'string' ? items : JSON.stringify(items, null, 2)}</Markdown>}
            </AccordionDetails>
        </Accordion>
    );
}

ResultAccordion.propTypes = {
    title: PropTypes.string,
    items: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
};

function ResultView({ result }) {
    const qaPairs = Array.isArray(result.conversation)
        ? result.conversation
        : [{ answer: result.answer }];

    return (
        <>
            {qaPairs.map((pair, index) => (
                <Box key={index}>
                    {index > 0 && <Divider sx={{ my: 2 }} />}
                    {pair.query && (
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{pair.query}</Typography>
                    )}
                    <Markdown>{pair.answer}</Markdown>
                </Box>
            ))}

            <ResultAccordion title="Summary IDs" items={result.summaryIds} />
            <ResultAccordion title="Relevant Summaries" items={result.relevantSummaries} />
            <ResultAccordion title="Relevant Source Texts" items={result.relevantSourceTexts} />
        </>
    );
}

ResultView.propTypes = {
    result: PropTypes.shape({
        answer: PropTypes.string,
        conversation: PropTypes.arrayOf(PropTypes.shape({
            query: PropTypes.string,
            answer: PropTypes.string,
        })),
        summaryIds: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
        relevantSummaries: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
        relevantSourceTexts: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
    }).isRequired,
};

export default function ContractsAICard() {
    const [modelOptions, setModelOptions] = useState([]);
    const [model, setModel] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [conversationsLoading, setConversationsLoading] = useState(false);
    const [conversationsError, setConversationsError] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [formKey, setFormKey] = useState(0);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        (async () => {
            try {
                const models = await GetContractsAIModels();
                const options = Object.entries(models || {}).map(([value, info]) => ({
                    value,
                    label: `${value} (${info.description})`,
                }));
                setModelOptions(options);
                if (options.length) setModel(options[0].value);
            } catch (err) {
                enqueueSnackbar('Failed to load Contracts AI models', { variant: 'error' });
            }
        })();
    }, [enqueueSnackbar]);

    const loadConversations = async () => {
        try {
            setConversationsLoading(true);
            const data = await GetUserConversations();
            if (!Array.isArray(data)) throw new Error('Failed to load conversations');
            setConversations(data);
            setConversationsError(false);
        } catch (err) {
            setConversations([]);
            setConversationsError(true);
            enqueueSnackbar('Failed to load previous conversations', { variant: 'error' });
        } finally {
            setConversationsLoading(false);
        }
    };

    useEffect(() => { loadConversations(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (query, model, files) => {
        try {
            setLoading(true);
            setResult(null);
            setSelectedId(null);
            const data = await PostToContractsAI(query, model, files);
            if (!data) throw new Error('Empty response from Contracts AI');
            setResult(data);
            loadConversations();
        } catch (err) {
            enqueueSnackbar('Failed to get a response from Contracts AI', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSelectConversation = async (conversationId) => {
        try {
            setSelectedId(conversationId);
            setLoading(true);
            setResult(null);
            const data = await GetConversation(conversationId);
            if (!data || !Array.isArray(data.conversation) || !data.conversation.length) {
                throw new Error('Empty response from Contracts AI');
            }
            setResult(data);
        } catch (err) {
            setResult(null);
            setSelectedId(null);
            enqueueSnackbar('Failed to load conversation', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleNewQuestion = () => {
        setSelectedId(null);
        setResult(null);
        setFormKey(k => k + 1);
    };

    return (
        <Box sx={{ display: 'flex', gap: 2, p: 3 }}>
            <ConversationSidebar
                conversations={conversations}
                selectedId={selectedId}
                loading={conversationsLoading}
                error={conversationsError}
                busy={loading}
                onSelect={handleSelectConversation}
            />

            <Card sx={{ flex: 1, p: 3, maxWidth: 700 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6">Ask Contracts AI</Typography>

                    {(selectedId || result) && (
                        <Button variant="outlined" onClick={handleNewQuestion} disabled={loading} sx={{ alignSelf: 'flex-start' }}>
                            Ask new question
                        </Button>
                    )}

                    {selectedId ? (
                        loading && <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    ) : (
                        <AskForm
                            key={formKey}
                            modelOptions={modelOptions}
                            model={model}
                            setModel={setModel}
                            loading={loading}
                            onSubmit={handleSubmit}
                        />
                    )}

                    {result && <ResultView result={result} />}
                </CardContent>
            </Card>
        </Box>
    );
}
